import React, { useState, useRef } from 'react';

/**
 * ChipInput - A tag/chip UI for entering multiple email addresses.
 *
 * Props:
 *   value: string[]         - current list of chips (controlled)
 *   onChange: (chips) => {} - called with updated chips array on every change
 *   placeholder: string     - placeholder text for the input field
 */
export default function ChipInput({ value = [], onChange, placeholder = 'Add email...' }) {
    const [inputValue, setInputValue] = useState('');
    const [error, setError] = useState('');
    const inputRef = useRef(null);

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const addChip = (text) => {
        const trimmed = text.trim();
        if (!trimmed) return;
        if (!isValidEmail(trimmed)) {
            setError('Please enter a valid email address.');
            return;
        }
        if (value.includes(trimmed)) {
            setError('This email has already been added.');
            return;
        }
        setError('');
        onChange([...value, trimmed]);
        setInputValue('');
    };

    const removeChip = (index) => {
        onChange(value.filter((_, i) => i !== index));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            addChip(inputValue);
        } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
            removeChip(value.length - 1);
        }
    };

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 8px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    backgroundColor: '#FFFFFF',
                    cursor: 'text',
                    minHeight: '42px',
                }}
                onClick={() => inputRef.current?.focus()}
            >
                {value.map((chip, i) => (
                    <span
                        key={i}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#EEF2FF',
                            color: '#3730A3',
                            borderRadius: '4px',
                            padding: '2px 8px',
                            fontSize: '12px',
                            fontWeight: 500,
                            gap: '6px',
                            userSelect: 'none',
                        }}
                    >
                        {chip}
                        <span
                            style={{
                                cursor: 'pointer',
                                color: '#6366F1',
                                fontWeight: 700,
                                fontSize: '14px',
                                lineHeight: '1',
                                padding: '0 2px',
                            }}
                            onClick={(e) => { e.stopPropagation(); removeChip(i); }}
                        >
                            &times;
                        </span>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="email"
                    value={inputValue}
                    placeholder={value.length === 0 ? placeholder : ''}
                    onChange={(e) => { setInputValue(e.target.value); setError(''); }}
                    onKeyDown={handleKeyDown}
                    onBlur={() => { if (inputValue.trim()) addChip(inputValue); }}
                    style={{
                        border: 'none',
                        outline: 'none',
                        fontSize: '13px',
                        color: '#1F2937',
                        flexGrow: 1,
                        minWidth: '140px',
                        backgroundColor: 'transparent',
                        padding: '2px 0',
                    }}
                />
            </div>
            {error && (
                <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>
                    {error}
                </div>
            )}
            <div style={{ color: '#94A3B8', fontSize: '11px', marginTop: '3px' }}>
                Press Enter or Tab to add each email
            </div>
        </div>
    );
}
